import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  AlertTriangle,
  RefreshCw,
  Plus,
  FileText,
  BarChart3,
  ArrowRight,
  Scale
} from 'lucide-react';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';
import { format } from 'date-fns';
import api, {
  getFinanceSummary, getExpenses, getIncome,
  getCashAccounts, createCashAccount, upsertBalanceEntry, getBalanceCheck,
  createFloatDeposit, getFloatDeposits, getFloatSummary, getOwnerSegments,
} from '../../services/api';
import PageHeader from '../../components/PageHeader';
import StatCard from '../../components/StatCard';
import { useAuth } from '../../contexts/AuthContext';
import { CustomTooltip, gridProps, axisTick, barRadius, chartMargin, CHART_PALETTE } from '../../components/ChartConfig';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-UG', {
    style: 'currency',
    currency: 'UGX',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

const formatShortCurrency = (amount) => {
  if (amount >= 1000000) return `${(amount / 1000000).toFixed(1)}M`;
  if (amount >= 1000) return `${(amount / 1000).toFixed(0)}K`;
  return amount.toString();
};

// Monday balances (S5): register cash accounts once, enter each balance
// every Monday. The weekly scorecard's balance check compares the movement
// against recorded paid money — this card is where the habit lives.
function MondayBalancesCard({ ranchId, canWrite }) {
  const queryClient = useQueryClient();
  const [newAccount, setNewAccount] = useState(null); // {name, account_type}
  const [balances, setBalances] = useState({});       // account_id -> input value

  const { data: accounts } = useQuery({
    queryKey: ['cashAccounts', ranchId],
    queryFn: () => getCashAccounts(ranchId),
    enabled: !!ranchId,
  });
  const { data: check } = useQuery({
    queryKey: ['balanceCheck', ranchId],
    queryFn: () => getBalanceCheck(ranchId),
    enabled: !!ranchId,
  });

  const addAccount = useMutation({
    mutationFn: (payload) => createCashAccount(payload),
    onSuccess: () => {
      setNewAccount(null);
      queryClient.invalidateQueries(['cashAccounts']);
      queryClient.invalidateQueries(['balanceCheck']);
    },
  });
  const enterBalance = useMutation({
    mutationFn: (payload) => upsertBalanceEntry(payload),
    onSuccess: () => queryClient.invalidateQueries(['balanceCheck']),
  });

  const items = accounts?.items || [];
  if (!items.length && !canWrite) return null;
  const byId = Object.fromEntries((check?.accounts || []).map((a) => [a.id, a]));

  return (
    <div className="card p-4">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <Scale className="h-5 w-5 text-primary-600" />
          <h2 className="font-semibold text-gray-900">Monday balances</h2>
          {check?.week_start && (
            <span className="text-xs text-earth-500">week of {check.week_start}</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {check?.clean === true && (
            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
              Reconciles
            </span>
          )}
          {check?.clean === false && (
            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800"
              title={`Cash moved ${check.actual_movement} but recorded money explains ${check.expected_movement}`}>
              Unexplained {formatCurrency(check.delta)}
            </span>
          )}
          {canWrite && !newAccount && (
            <button onClick={() => setNewAccount({ name: '', account_type: 'cash' })}
              className="text-sm text-primary-700 hover:underline">
              + Add account
            </button>
          )}
        </div>
      </div>

      {items.length === 0 && (
        <p className="text-sm text-earth-500 mb-2">
          Register the ranch's cash locations (MoMo float, cash box, bank) to start the
          Monday balance habit — the weekly scorecard begins checking once one exists.
        </p>
      )}

      <div className="space-y-2">
        {items.map((a) => {
          const entry = byId[a.id];
          return (
            <div key={a.id} className="flex flex-wrap items-center justify-between gap-2 text-sm border-b border-earth-100 pb-2">
              <div>
                <span className="font-medium">{a.name}</span>
                <span className="text-earth-500"> · {a.account_type}</span>
              </div>
              <div className="flex items-center gap-2">
                {entry?.entered ? (
                  <span className="text-gray-800">
                    {formatCurrency(entry.balance)}
                    {entry.entry_source === 'momo_api' && (
                      <span className="text-xs text-earth-500"> (auto)</span>
                    )}
                  </span>
                ) : (
                  <span className="text-amber-700 text-xs">no balance this week</span>
                )}
                {canWrite && (
                  <form onSubmit={(e) => {
                    e.preventDefault();
                    const val = parseFloat(balances[a.id]);
                    if (!Number.isFinite(val) || val < 0) return;
                    enterBalance.mutate({ ranch_id: ranchId, account_id: a.id, balance: val });
                  }} className="flex items-center gap-1">
                    <input type="number" min="0" step="1" placeholder="Balance"
                      value={balances[a.id] || ''}
                      onChange={(e) => setBalances({ ...balances, [a.id]: e.target.value })}
                      className="w-28 border border-earth-300 rounded px-2 py-1 text-sm" />
                    <button type="submit" disabled={enterBalance.isPending}
                      className="px-2 py-1 rounded bg-primary-600 text-white text-xs hover:bg-primary-700 disabled:opacity-40">
                      Save
                    </button>
                  </form>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {enterBalance.isError && (
        <p className="text-xs text-red-600 mt-2">
          {enterBalance.error?.response?.data?.detail || 'Could not save the balance.'}
        </p>
      )}

      {canWrite && newAccount && (
        <form onSubmit={(e) => {
          e.preventDefault();
          if (!newAccount.name.trim()) return;
          addAccount.mutate({ ranch_id: ranchId, ...newAccount, name: newAccount.name.trim() });
        }} className="mt-3 flex flex-wrap items-center gap-2 text-sm">
          <input autoFocus placeholder="Account name (e.g. MTN MoMo float)"
            value={newAccount.name}
            onChange={(e) => setNewAccount({ ...newAccount, name: e.target.value })}
            className="border border-earth-300 rounded px-2 py-1" />
          <select value={newAccount.account_type}
            onChange={(e) => setNewAccount({ ...newAccount, account_type: e.target.value })}
            className="border border-earth-300 rounded px-2 py-1">
            <option value="cash">Cash</option>
            <option value="momo">Mobile money</option>
            <option value="bank">Bank</option>
            <option value="other">Other</option>
          </select>
          <button type="submit" disabled={addAccount.isPending}
            className="px-3 py-1 rounded bg-primary-600 text-white text-xs hover:bg-primary-700 disabled:opacity-40">
            Create
          </button>
          <button type="button" onClick={() => setNewAccount(null)}
            className="text-xs text-earth-500 hover:underline">Cancel</button>
        </form>
      )}
    </div>
  );
}

// Owner float deposits (mig 118): owners prepay the manager's operating
// float; expenses paid from the float draw each owner's balance down by the
// quarterly ratio. This card is where the manager records money as it lands.
function OwnerDepositsCard({ ranchId, canWrite }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState(null); // {owner_segment_id, account_id, amount, deposit_date, method, reference}

  const { data: summary } = useQuery({
    queryKey: ['floatSummary', ranchId],
    queryFn: () => getFloatSummary(ranchId),
    enabled: !!ranchId,
  });
  const { data: deposits } = useQuery({
    queryKey: ['floatDeposits', ranchId],
    queryFn: () => getFloatDeposits(ranchId, 8),
    enabled: !!ranchId,
  });
  const { data: accounts } = useQuery({
    queryKey: ['cashAccounts', ranchId],
    queryFn: () => getCashAccounts(ranchId),
    enabled: !!ranchId,
  });
  const { data: segments } = useQuery({
    // ', all' suffix = the includeEmpty shape (repo convention) — the bare
    // key is the filtered variant used by IncomeForm/DirectorAccounts, and
    // sharing it would poison their cache with phantom empty owners.
    queryKey: ['ownerSegments', ranchId, 'all'],
    queryFn: () => getOwnerSegments(ranchId, { includeEmpty: true }),
    enabled: !!ranchId,
  });

  const addDeposit = useMutation({
    mutationFn: (payload) => createFloatDeposit(payload),
    onSuccess: () => {
      setForm(null);
      queryClient.invalidateQueries(['floatSummary']);
      queryClient.invalidateQueries(['floatDeposits']);
      queryClient.invalidateQueries(['balanceCheck']);
    },
  });

  const accountItems = accounts?.items || [];
  const ownerItems = (segments || []).filter((s) => !s.is_company);
  const balances = summary?.balances || [];
  const hasActivity = summary?.uses_float || (deposits?.items || []).length > 0;
  if (!canWrite && !hasActivity) return null;

  return (
    <div className="card p-4">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-primary-600" />
          <h2 className="font-semibold text-gray-900">Owner contributions (float)</h2>
        </div>
        {canWrite && !form && accountItems.length > 0 && ownerItems.length > 0 && (
          <button
            onClick={() => setForm({
              owner_segment_id: ownerItems[0]?.id || '',
              account_id: accountItems[0]?.id || '',
              amount: '', deposit_date: format(new Date(), 'yyyy-MM-dd'),
              method: 'momo', reference: '',
            })}
            className="text-sm text-primary-700 hover:underline"
          >
            + Record deposit
          </button>
        )}
      </div>

      <p className="text-sm text-earth-500 mb-3">
        Money owners send to the farm float. Expenses paid from the float draw each
        owner's balance down by the quarterly herd-share ratio.
      </p>

      {canWrite && accountItems.length === 0 && (
        <p className="text-sm text-amber-700 mb-2">
          Register a cash account (Monday balances card) before recording deposits.
        </p>
      )}

      {form && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            const amt = parseFloat(form.amount);
            if (!Number.isFinite(amt) || amt <= 0) return;
            addDeposit.mutate({
              ranch_id: ranchId,
              owner_segment_id: form.owner_segment_id,
              account_id: form.account_id,
              amount: amt,
              deposit_date: form.deposit_date,
              method: form.method,
              reference: form.reference || null,
            });
          }}
          className="flex flex-wrap items-end gap-2 mb-4 p-3 bg-earth-50 rounded-lg"
        >
          <div>
            <label className="block text-xs text-earth-500 mb-1">Owner</label>
            <select value={form.owner_segment_id}
              onChange={(e) => setForm({ ...form, owner_segment_id: e.target.value })}
              className="border border-earth-300 rounded px-2 py-1 text-sm">
              {ownerItems.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-earth-500 mb-1">Into account</label>
            <select value={form.account_id}
              onChange={(e) => setForm({ ...form, account_id: e.target.value })}
              className="border border-earth-300 rounded px-2 py-1 text-sm">
              {accountItems.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-earth-500 mb-1">Amount (UGX)</label>
            <input type="number" min="1" step="1" value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              className="w-32 border border-earth-300 rounded px-2 py-1 text-sm" />
          </div>
          <div>
            <label className="block text-xs text-earth-500 mb-1">Date</label>
            <input type="date" value={form.deposit_date}
              onChange={(e) => setForm({ ...form, deposit_date: e.target.value })}
              className="border border-earth-300 rounded px-2 py-1 text-sm" />
          </div>
          <div>
            <label className="block text-xs text-earth-500 mb-1">MoMo ref</label>
            <input type="text" value={form.reference} placeholder="optional"
              onChange={(e) => setForm({ ...form, reference: e.target.value })}
              className="w-32 border border-earth-300 rounded px-2 py-1 text-sm" />
          </div>
          <button type="submit" disabled={addDeposit.isPending}
            className="px-3 py-1.5 rounded bg-primary-600 text-white text-sm hover:bg-primary-700 disabled:opacity-40">
            Save
          </button>
          <button type="button" onClick={() => setForm(null)}
            className="px-3 py-1.5 rounded bg-earth-100 text-earth-700 text-sm hover:bg-earth-200">
            Cancel
          </button>
          {addDeposit.isError && (
            <span className="text-xs text-red-600">
              {addDeposit.error?.response?.data?.detail || 'Failed to record deposit'}
            </span>
          )}
        </form>
      )}

      {summary?.awaiting_payment?.count > 0 && (
        <p className="text-xs text-amber-700 mb-2">
          {summary.awaiting_payment.count} approved float expense(s) totalling{' '}
          {formatCurrency(summary.awaiting_payment.total)} are awaiting payment
          recording (Expenses page → Pay) — balances draw down at payment.
        </p>
      )}

      {balances.length > 0 && hasActivity && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-3">
          {balances.map((b) => (
            <div key={b.owner_segment_id}
              className={`p-2 rounded-lg border text-sm ${b.in_arrears
                ? 'bg-red-50 border-red-200' : 'bg-earth-50 border-earth-100'}`}>
              <p className="text-earth-600">{b.name}</p>
              <p className={`font-semibold ${b.balance < 0 ? 'text-red-700' : 'text-gray-900'}`}>
                {formatCurrency(b.balance)}
              </p>
              {b.pending_drawdown > 0 && (
                <p className="text-xs text-amber-700">
                  committed {formatCurrency(b.pending_drawdown)}
                </p>
              )}
              {b.in_arrears && (
                <p className="text-xs text-red-600">behind on contributions</p>
              )}
            </div>
          ))}
        </div>
      )}

      {(deposits?.items || []).length > 0 && (
        <div className="text-sm">
          <p className="text-xs text-earth-500 mb-1">Recent deposits</p>
          {deposits.items.map((d) => (
            <div key={d.id} className="flex items-center justify-between border-b border-earth-100 py-1">
              <span>{d.deposit_date} · <span className="font-medium">{d.owner_name}</span>
                {d.reference ? <span className="text-earth-500"> · {d.reference}</span> : null}</span>
              <span className="text-gray-800">{formatCurrency(d.amount)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function FinanceDashboard({ ranchId }) {
  const queryClient = useQueryClient();
  const { canWrite } = useAuth();
  const [selectedPeriod, setSelectedPeriod] = useState('month');
  // One-shot notice passed from ExpenseForm ("awaiting owner approval").
  const location = useLocation();
  const navigate = useNavigate();
  const [notice, setNotice] = useState(location.state?.notice || '');
  useEffect(() => {
    if (!notice) return;
    // Clear the entry's state through the router (raw history.replaceState
    // would stomp react-router's own bookkeeping) so refresh/back doesn't
    // re-show the notice.
    if (location.state?.notice) {
      navigate(location.pathname, { replace: true, state: null });
    }
    const t = setTimeout(() => setNotice(''), 8000);
    return () => clearTimeout(t);
  }, [notice]);

  const { data: summary, isLoading: summaryLoading } = useQuery({
    queryKey: ['financeSummary', ranchId, selectedPeriod],
    queryFn: () => getFinanceSummary(ranchId, selectedPeriod),
  });

  const { data: recentExpenses } = useQuery({
    queryKey: ['recentExpenses', ranchId],
    queryFn: () => getExpenses(ranchId, { page_size: 5 }),
  });

  const { data: recentIncome } = useQuery({
    queryKey: ['recentIncome', ranchId],
    queryFn: () => getIncome(ranchId, { page_size: 100 }),
  });

  const { data: anomalies } = useQuery({
    queryKey: ['anomalies', ranchId],
    queryFn: async () => {
      try {
        const res = await api.get('/finance/agents/anomalies', { params: { ranch_id: ranchId, status: 'open' } });
        return res.data;
      } catch { return []; }
    },
  });

  const { data: periodSummaries } = useQuery({
    queryKey: ['periodSummaries', ranchId],
    queryFn: async () => {
      try {
        const res = await api.get('/finance/agents/summaries', { params: { ranch_id: ranchId, period_type: 'monthly', limit: 6 } });
        return res.data;
      } catch { return []; }
    },
  });

  const closePeriodMutation = useMutation({
    mutationFn: async () => {
      const periodMap = { 'week': 'weekly', 'month': 'monthly', 'quarter': 'quarterly', 'year': 'yearly' };
      const res = await api.post('/finance/agents/close-period', null, { params: { ranch_id: ranchId, period_type: periodMap[selectedPeriod] || 'monthly' } });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['periodSummaries']);
      queryClient.invalidateQueries(['anomalies']);
    },
  });

  const detectAnomaliesMutation = useMutation({
    mutationFn: async () => {
      const res = await api.post('/finance/agents/detect-anomalies', null, { params: { ranch_id: ranchId, days: 30 } });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['anomalies']);
    },
  });

  const unpaidExpenses = recentExpenses?.items?.filter(
    e => e.approval_status === 'approved' && e.payment_status !== 'paid'
  )?.length || 0;

  const unpaidIncome = recentIncome?.items?.filter(
    i => i.payment_status !== 'paid'
  )?.length || 0;

  // Expense breakdown by category
  const expenseByCategory = summary?.by_category
    ? Object.entries(summary.by_category)
        .map(([name, amount]) => ({ name, value: amount }))
        .sort((a, b) => b.value - a.value)
    : [];
  const maxCatValue = expenseByCategory.length > 0 ? expenseByCategory[0].value : 1;

  // Monthly trend with net income line
  const monthlyTrend = periodSummaries
    ? [...periodSummaries].reverse().map(s => ({
        month: format(new Date(s.period_start), 'MMM'),
        income: s.total_income,
        expenses: s.total_expenses,
        net: s.total_income - s.total_expenses,
      }))
    : [];

  const netIncome = summary?.net_income || 0;

  if (summaryLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {notice && (
        <div className="p-3 bg-blue-50 border border-blue-200 text-blue-800 rounded-lg text-sm">
          {notice}
        </div>
      )}
      <PageHeader
        title="Financial Overview"
        subtitle="Track income, expenses, and financial health"
        icon={DollarSign}
        actions={
          <>
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              className="input w-32"
            >
              <option value="week">This Week</option>
              <option value="month">This Month</option>
              <option value="quarter">This Quarter</option>
              <option value="year">This Year</option>
            </select>
            <Link to="/finance/director-accounts" className="btn btn-secondary">
              <Scale className="h-4 w-4 mr-2" />
              Director Accounts
            </Link>
            {canWrite && (
              <>
                <Link to="/income/new" className="btn bg-green-600 text-white hover:bg-green-700">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Income
                </Link>
                <Link to="/expenses/new" className="btn btn-primary">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Expense
                </Link>
              </>
            )}
          </>
        }
      />

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Income"
          value={formatCurrency(summary?.total_income || 0)}
          icon={TrendingUp}
          color="green"
        />
        <StatCard
          title="Total Expenses"
          value={formatCurrency(summary?.total_expenses || 0)}
          icon={TrendingDown}
          color="red"
        />
        <StatCard
          title="Net Income"
          value={formatCurrency(netIncome)}
          icon={DollarSign}
          color={netIncome >= 0 ? 'green' : 'red'}
          valueColor={netIncome >= 0 ? 'text-green-600' : 'text-red-600'}
        />
        <StatCard
          title="Open Alerts"
          value={anomalies?.length || 0}
          icon={AlertTriangle}
          color="amber"
          subtitle={unpaidExpenses > 0 ? `${unpaidExpenses} unpaid expenses` : undefined}
        />
      </div>

      {/* Pending Payments */}
      {(unpaidExpenses > 0 || unpaidIncome > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Link to="/expenses" className="card p-4 hover:shadow-md transition-shadow group">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-50 rounded-xl ring-1 ring-amber-100">
                  <TrendingDown className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-700">Unpaid Expenses</div>
                  <div className="text-xl font-bold text-amber-700">{unpaidExpenses}</div>
                </div>
              </div>
              <ArrowRight className="h-5 w-5 text-earth-400 group-hover:text-primary-600 transition-colors" />
            </div>
          </Link>
          <Link to="/income" className="card p-4 hover:shadow-md transition-shadow group">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-50 rounded-xl ring-1 ring-amber-100">
                  <TrendingUp className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-700">Unpaid Income</div>
                  <div className="text-xl font-bold text-amber-700">{unpaidIncome}</div>
                </div>
              </div>
              <ArrowRight className="h-5 w-5 text-earth-400 group-hover:text-primary-600 transition-colors" />
            </div>
          </Link>
        </div>
      )}

      {/* Monday balances (S5 balance check) */}
      <MondayBalancesCard ranchId={ranchId} canWrite={canWrite} />

      {/* Owner float deposits (mig 118) */}
      <OwnerDepositsCard ranchId={ranchId} canWrite={canWrite} />

      {/* Expenses missing receipts */}
      {summary?.missing_receipt_count > 0 && (
        <Link to="/expenses" className="card p-4 hover:shadow-md transition-shadow group block">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-50 rounded-xl ring-1 ring-amber-100">
                <AlertTriangle className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <div className="text-sm font-medium text-gray-700">Expenses without a receipt</div>
                <div className="text-xl font-bold text-amber-700">
                  {summary.missing_receipt_count}
                  <span className="ml-2 text-sm font-normal text-gray-500">
                    ({formatCurrency(summary.missing_receipt_total || 0)})
                  </span>
                </div>
              </div>
            </div>
            <ArrowRight className="h-5 w-5 text-earth-400 group-hover:text-primary-600 transition-colors" />
          </div>
        </Link>
      )}

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Expense Breakdown */}
        <div className="section-card">
          <div className="section-card-header">
            <h3 className="section-card-title">Expenses by Category</h3>
            <Link to="/expenses" className="text-sm text-primary-600 hover:text-primary-800">View all</Link>
          </div>
          <div className="section-card-body">
            {expenseByCategory.length > 0 ? (
              <div className="space-y-3">
                {expenseByCategory.map((cat, i) => (
                  <div key={cat.name} className="flex items-center gap-3">
                    <div className="w-24 text-sm text-earth-600 truncate capitalize">{cat.name}</div>
                    <div className="flex-1 h-3 bg-earth-100 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{
                          width: `${(cat.value / maxCatValue) * 100}%`,
                          backgroundColor: CHART_PALETTE[i % CHART_PALETTE.length]
                        }}
                      />
                    </div>
                    <div className="w-28 text-sm font-semibold text-gray-900 text-right">
                      {formatCurrency(cat.value)}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="h-48 flex items-center justify-center text-earth-500">
                No expense data for this period
              </div>
            )}
          </div>
        </div>

        {/* Monthly Trend */}
        <div className="section-card">
          <div className="section-card-header">
            <h3 className="section-card-title">Monthly Trend</h3>
            <BarChart3 className="h-5 w-5 text-earth-400" />
          </div>
          <div className="section-card-body">
            {monthlyTrend.length > 0 ? (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={monthlyTrend} margin={chartMargin}>
                    <defs>
                      <linearGradient id="incomeGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#2d4a3e" stopOpacity={0.9} />
                        <stop offset="100%" stopColor="#2d4a3e" stopOpacity={0.6} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid {...gridProps} />
                    <XAxis dataKey="month" tick={axisTick} />
                    <YAxis tickFormatter={formatShortCurrency} tick={axisTick} />
                    <Tooltip content={<CustomTooltip formatter={(v) => formatCurrency(v)} />} />
                    <Legend />
                    <Bar dataKey="income" name="Income" fill="url(#incomeGrad)" radius={barRadius} />
                    <Bar dataKey="expenses" name="Expenses" fill="#ef4444" radius={barRadius} opacity={0.75} />
                    <Line dataKey="net" name="Net" stroke="#b8954f" strokeWidth={2} dot={false} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-earth-500">
                <div className="text-center">
                  <p>No period summaries yet</p>
                  {canWrite && (
                    <button
                      onClick={() => closePeriodMutation.mutate()}
                      disabled={closePeriodMutation.isPending}
                      className="btn btn-primary mt-3"
                    >
                      <RefreshCw className={`h-4 w-4 mr-2 ${closePeriodMutation.isPending ? 'animate-spin' : ''}`} />
                      Generate Summary
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Expenses */}
        <div className="section-card">
          <div className="section-card-header">
            <h3 className="section-card-title">Recent Expenses</h3>
            <div className="flex gap-3">
              <Link to="/expenses" className="text-sm text-primary-600 hover:text-primary-800">Expenses</Link>
              <Link to="/income" className="text-sm text-primary-600 hover:text-primary-800">Income</Link>
            </div>
          </div>
          <div className="section-card-body">
            {recentExpenses?.items?.length > 0 ? (
              <div className="space-y-2">
                {recentExpenses.items.map((expense) => (
                  <div key={expense.id} className="flex items-center justify-between p-3 bg-earth-50 rounded-lg hover:bg-earth-100 transition-colors">
                    <div>
                      <p className="font-medium text-gray-900">{expense.description}</p>
                      <p className="text-sm text-earth-500">
                        {expense.category_name} &middot; {format(new Date(expense.expense_date), 'MMM d')}
                      </p>
                    </div>
                    <span className="font-semibold text-red-600">
                      -{formatCurrency(expense.amount)}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-earth-500 text-center py-4">No recent expenses</p>
            )}
          </div>
        </div>

        {/* Financial Alerts */}
        <div className="section-card">
          <div className="section-card-header">
            <h3 className="section-card-title">Financial Alerts</h3>
            {canWrite && (
              <button
                onClick={() => detectAnomaliesMutation.mutate()}
                disabled={detectAnomaliesMutation.isPending}
                className="text-sm text-primary-600 hover:text-primary-800 flex items-center gap-1"
              >
                <RefreshCw className={`h-4 w-4 ${detectAnomaliesMutation.isPending ? 'animate-spin' : ''}`} />
                Scan
              </button>
            )}
          </div>
          <div className="section-card-body">
            {anomalies?.length > 0 ? (
              <div className="space-y-2">
                {anomalies.slice(0, 5).map((anomaly) => (
                  <div
                    key={anomaly.id}
                    className={`p-3 rounded-lg border-l-4 ${
                      anomaly.severity === 'high'
                        ? 'bg-red-50 border-red-500'
                        : anomaly.severity === 'medium'
                        ? 'bg-amber-50 border-amber-500'
                        : 'bg-blue-50 border-blue-500'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium text-gray-900 text-sm">
                          {anomaly.anomaly_type.replace('_', ' ').toUpperCase()}
                        </p>
                        <p className="text-sm text-earth-600 mt-1">{anomaly.description}</p>
                      </div>
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                        anomaly.severity === 'high'
                          ? 'bg-red-100 text-red-700'
                          : anomaly.severity === 'medium'
                          ? 'bg-amber-100 text-amber-700'
                          : 'bg-blue-100 text-blue-700'
                      }`}>
                        {anomaly.severity}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-earth-500">
                <AlertTriangle className="h-12 w-12 mx-auto mb-3 opacity-20" />
                <p className="font-medium">No alerts detected</p>
                <p className="text-sm mt-1">Your finances look healthy</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Agent Actions */}
      {canWrite && (
      <div className="section-card">
        <div className="section-card-header">
          <h3 className="section-card-title">Financial Agents</h3>
        </div>
        <div className="section-card-body">
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => closePeriodMutation.mutate()}
              disabled={closePeriodMutation.isPending}
              className="btn bg-primary-100 text-primary-700 hover:bg-primary-200"
            >
              <FileText className="h-4 w-4 mr-2" />
              {closePeriodMutation.isPending ? 'Running...' : `Close ${selectedPeriod}`}
            </button>
            <button
              onClick={() => detectAnomaliesMutation.mutate()}
              disabled={detectAnomaliesMutation.isPending}
              className="btn bg-amber-100 text-amber-700 hover:bg-amber-200"
            >
              <AlertTriangle className="h-4 w-4 mr-2" />
              {detectAnomaliesMutation.isPending ? 'Scanning...' : 'Detect Anomalies'}
            </button>
          </div>
          {(closePeriodMutation.isSuccess || detectAnomaliesMutation.isSuccess) && (
            <div className="mt-4 p-3 bg-green-50 text-green-700 rounded-lg text-sm border border-green-100">
              Agent completed successfully
            </div>
          )}
        </div>
      </div>
      )}
    </div>
  );
}
